import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { navigate } from "vike/client/router";
import pluralize from "pluralize";
import { addClubBook, removeClubBook } from "#redux/slices/clubSlice";
import { openModal } from "#redux/slices/globalUISlice";
import Tag from "#helpers/Tag";
import Button from "#helpers/Button";
import IconButton from "#helpers/IconButton";
import BookCard from "#cards/BookCard";
import AddBookCard from "#cards/AddBookCard";
import shareIcon from "#assets/shareIcon.svg";
import { getMaterialIcon } from "#utils/icons";
import { useShareLink } from "#utils/hooks";
import EditClub from "./EditClub";
const EditIcon = getMaterialIcon("Edit");
const RemoveCircleIcon = getMaterialIcon("RemoveCircle");

const AboutUsModeratorView = ({ club, refetchClub }) => {
  const dispatch = useDispatch();
  const shareLink = useShareLink();
  const [isEditingAboutUs, setIsEditingAboutUs] = useState(false);
  const [isEditingShowcase, setIsEditingShowcase] = useState(false);

  const membersText = `${pluralize(club.member_nickname || "members", club.member_count, true)} joined`;
  const onViewMembers = () => {
    dispatch(openModal({ name: "viewMembers", data: { club: club } }));
  };

  const onClickAddBookCard = () => {
    const handleClick = (book) => {
      if (club.showcased_books.length < 10) {
        dispatch(
          addClubBook({
            book_id: book.id,
            showcase: true,
            club_uuid: club.uuid,
          })
        ).unwrap().then(refetchClub);
      }
    };
    const addedBookIds = club.showcased_books.map((b) => b.id);
    dispatch(openModal({ name: "searchReturnBook", data: { addedBookIds, handleClick } }));
  };

  const removeBookIcon = (book) => {
    return (
      <button
        type="button"
        onClick={() =>
          dispatch(
            removeClubBook({
              book_id: book.id,
              club_uuid: club.uuid,
            })
          ).unwrap().then(refetchClub)
        }
      >
        <RemoveCircleIcon fontSize="small" sx={{ color: "#FF695F" }} />
      </button>
    );
  };

  const handleShare = (event) => {
    event.stopPropagation();
    shareLink(`/clubs/${club.uuid}`, "Join me in this club on Pagebound!");
  };

  return (
    <div className="flex flex-col gap-8 md:gap-16 md:justify-start md:items-start">
      {!isEditingAboutUs && (
        <div className="flex flex-col gap-2 pl-4 md:pl-0 w-full">
          <div className="flex gap-3 md:gap-6 text-left items-center justify-between w-full">
            <h2>About Us</h2>
            <div className="flex gap-2">
              <IconButton
                IconComponent={EditIcon}
                onClick={() => setIsEditingAboutUs(true)}
                tooltip="Edit"
              />
              <IconButton onClick={handleShare} tooltip="Share">
                <img src={shareIcon} alt="share" className="w-[12px] h-[12px] md:w-[17px] md:h-[17px]" />
              </IconButton>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
            <p>
              created by <a href={`/users/${club.creator_username}`}>{club.creator_username}</a>
            </p>
            <p className="hidden md:block">//</p>
            <button className="flex flex-row gap-2 cursor-pointer" onClick={onViewMembers}>
              <b className="hover:text-pink">{membersText}</b>
              {club.pending_member_count > 0 && (
                <Tag text={`${club.pending_member_count} pending`} color="bg-red" />
              )}
            </button>
          </div>
          <div className="flex gap-2">
            {club.emojis.map((emoji, index) => (
              <div className="text-[25px]" key={index}>
                {emoji}
              </div>
            ))}
          </div>
        </div>
      )}
      {isEditingAboutUs && <EditClub club={club} setIsEditing={setIsEditingAboutUs} />}
      <div className="flex flex-col gap-3">
        <div className="flex gap-6 md:gap-8 pl-4 md:pl-0 items-start justify-start">
          <div className="flex gap-3 md:gap-6 items-center">
            <h2>Our Taste</h2>
            <div className="flex gap-4 items-center">
              {!isEditingShowcase && (
                <IconButton
                  IconComponent={EditIcon}
                  onClick={() => setIsEditingShowcase(true)}
                  tooltip="Edit"
                />
              )}
              {isEditingShowcase && (
                <Button text="Done" onClick={() => setIsEditingShowcase(false)} />
              )}
              <Button text="Add More Recs" onClick={() => navigate(`/clubs/${club.uuid}/recommendations`)} color="bg-fuschia" />
            </div>
          </div>
        </div>
        {club.taste_description && <p className="pl-4 md:pl-0 text-left">{club.taste_description}</p>}
        <div className="flex flex-wrap gap-6 mt-4 justify-center items-center md:justify-start">
          {club.showcased_books.map((book) => (
            <BookCard key={book.id} book={book} icon={isEditingShowcase ? removeBookIcon(book) : null} />
          ))}
          {isEditingShowcase && club.showcased_books.length < 10 && <AddBookCard onClick={onClickAddBookCard} />}
        </div>
      </div>
    </div>
  );
};

export default AboutUsModeratorView;
